import axiosInstance from '../lib/api/axiosInstance';
import {
  ServiceSubscription,
  CreateServiceSubscriptionRequest,
  UpdateServiceSubscriptionRequest,
  ServiceType,
  ServiceSolution,
  ServiceStatus,
} from '../types/service';

// Interface pour les types de services disponibles
export interface ServiceTypesResponse {
  serviceTypes: ServiceType[];
  solutions: ServiceSolution[];
  statuses: ServiceStatus[];
}

// Interface pour la réponse des services d'un domaine
export interface DomainServicesResponse {
  domain: {
    id: number;
    name: string;
    url: string;
    network: {
      id: number;
      name: string;
      bqKey: string;
    };
  };
  services: ServiceSubscription[];
}

/**
 * Récupère tous les services d'un domaine
 */
export const getServicesByDomain = async (domainId: number): Promise<DomainServicesResponse> => {
  try {
    const response = await axiosInstance.get<DomainServicesResponse>(`/services/domain/${domainId}`);
    return response.data;
  } catch (error) {
    console.error(`Erreur lors de la récupération des services du domaine ${domainId}:`, error);
    throw error;
  }
};

/**
 * Crée un nouveau service
 */
export const createService = async (serviceData: CreateServiceSubscriptionRequest): Promise<ServiceSubscription> => {
  try {
    const response = await axiosInstance.post<ServiceSubscription>('/services', serviceData);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la création du service:', error);
    throw error;
  }
};

/**
 * Met à jour un service
 */
export const updateService = async (id: number, serviceData: UpdateServiceSubscriptionRequest): Promise<ServiceSubscription> => {
  try {
    const response = await axiosInstance.put<ServiceSubscription>(`/services/${id}`, serviceData);
    return response.data;
  } catch (error) {
    console.error(`Erreur lors de la mise à jour du service ${id}:`, error);
    throw error;
  }
};

/**
 * Supprime un service
 */
export const deleteService = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/services/${id}`);
  } catch (error) {
    console.error(`Erreur lors de la suppression du service ${id}:`, error);
    throw error;
  }
};

/**
 * Récupère les types de services disponibles
 */
export const getServiceTypes = async (): Promise<ServiceTypesResponse> => {
  try {
    const response = await axiosInstance.get<ServiceTypesResponse>('/services/types');
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des types de services:', error);
    throw error;
  }
};

// Interface pour la création rapide avec domaine
export interface QuickCreateWithDomainRequest {
  networkId: number;
  domainName: string;
  domainUrl?: string;
  service: ServiceType;
  solution: ServiceSolution;
  status?: ServiceStatus;
  startDate: string;
  endDate?: string | null;
  details?: string | null;
  // Config spécifiques selon la solution
  commissionPercentage?: number;
  commissionPublisher?: number;
  commissionDblock?: number;
  gamType?: string;
  gamAccountId?: string;
  scriptVariant?: string;
  integrationType?: string;
  protocol?: string;
  outbrainSection?: string;
  siteKey?: string;
  websiteName?: string;
}

export interface QuickCreateWithDomainResponse {
  domain: {
    id: number;
    name: string;
    url: string;
    networkId: number;
    created: boolean; // true si le domaine a été créé, false s'il existait déjà
  };
  service: ServiceSubscription;
}

/**
 * Crée un domaine ET un service en une seule opération
 * Utilisé depuis l'audit pour ajouter rapidement un service manquant avec création automatique du domaine
 */
export const quickCreateWithDomain = async (data: QuickCreateWithDomainRequest): Promise<QuickCreateWithDomainResponse> => {
  try {
    const response = await axiosInstance.post<QuickCreateWithDomainResponse>('/services/quick-create-with-domain', data);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la création rapide domaine + service:', error);
    throw error;
  }
};